import React, { useState } from 'react';
import { useEffect } from 'react';
import { Search, Filter, Globe, Linkedin, Phone, Smartphone, Mail, CheckCircle, X, Users, Building, UserCheck, Target, AlertCircle, Loader2 } from 'lucide-react';
import { useGoogleSheets } from '../../hooks/useGoogleSheets';
import StatsCard from '../Dashboard/StatsCard';

const PreQualifiedLeadsPage: React.FC = () => {
  const { data: preQualifiedLeads, isLoading, error, isAuthenticated } = useGoogleSheets('pre-qualified');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const filteredLeads = preQualifiedLeads.filter(lead => {
    const matchesSearch = (lead.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.dm_email_1 || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (lead.industry_1 || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'company_qualified' && lead.company_qualified) ||
                         (statusFilter === 'dm_qualified' && lead.dm_qualified) ||
                         (statusFilter === 'not_qualified' && (!lead.company_qualified || !lead.dm_qualified));
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, startIndex + itemsPerPage);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Calculate statistics
  const totalPreQualifiedLeads = preQualifiedLeads.length;
  const companyQualifiedCount = preQualifiedLeads.filter(lead => lead.company_qualified).length;
  const dmQualifiedCount = preQualifiedLeads.filter(lead => lead.dm_qualified).length;
  const needsEnrichmentCount = preQualifiedLeads.filter(lead => 
    lead.lead_status?.toLowerCase() === 'needs enrichment'
  ).length;

  // Only show loading if we're loading AND have no data to display
  if (isLoading && preQualifiedLeads.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-gray-500 text-lg mb-2">Loading data...</div>
          <div className="text-gray-400 text-sm">Please Make Sure The System Is Connected</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Status Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded-lg flex items-center space-x-2 mb-6">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Pre-Qualified Leads"
          value={totalPreQualifiedLeads}
          icon={Users}
          color="blue"
        />
        <StatsCard
          title="Company Qualified"
          value={companyQualifiedCount}
          icon={Building}
          color="green"
        />
        <StatsCard
          title="DM Qualified"
          value={dmQualifiedCount}
          icon={UserCheck}
          color="purple"
        />
        <StatsCard
          title="Needs Enrichment"
          value={needsEnrichmentCount}
          icon={Target}
          color="orange"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search pre-qualified leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="company_qualified">Company Qualified</option>
                <option value="dm_qualified">DM Qualified</option>
                <option value="not_qualified">Not Qualified</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  Company Name
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  Industry & Products
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                  Person
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                  Contact Details
                </th>
                <th className="text-left px-3 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                  Qualification & Verification
                </th>
                <th className="text-left px-3 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Lead Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedLeads.map((lead) => (
                <tr key={lead.id || lead.dm_email_1} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900 mb-2">{lead.company_name || 'N/A'}</div>
                    <div className="flex items-center space-x-2">
                      {lead.company_website && (
                        <a 
                          href={lead.company_website.startsWith('http') ? lead.company_website : `https://${lead.company_website}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Visit Website"
                        >
                          <Globe size={16} />
                        </a>
                      )}
                      {lead.company_linkedin && (
                        <a 
                          href={lead.company_linkedin.startsWith('http') ? lead.company_linkedin : `https://${lead.company_linkedin}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="LinkedIn Profile"
                        >
                          <Linkedin size={16} />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 break-words">
                      {lead.industry_1 && lead.industry_1.trim() ? (
                        lead.industry_1
                      ) : (
                        <span className="text-gray-400 italic flex items-center space-x-1">
                          <Loader2 size={12} className="animate-spin" />
                          <span>Analyzing...</span>
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 mt-1 break-words">
                      {lead.products_services && lead.products_services.trim() ? (
                        lead.products_services
                      ) : (
                        <span className="text-gray-400 italic flex items-center space-x-1">
                          <Loader2 size={12} className="animate-spin" />
                          <span>Analyzing...</span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium text-gray-900">
                      {lead.dm_full_name && lead.dm_full_name.trim() ? (
                        lead.dm_full_name
                      ) : (
                        <span className="text-gray-400 italic flex items-center space-x-1">
                          <Search size={12} className="animate-pulse" />
                          <span>Searching...</span>
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 break-words">
                      {lead.dm_title && lead.dm_title.trim() ? (
                        lead.dm_title
                      ) : (
                        lead.dm_full_name && lead.dm_full_name.trim() ? (
                          <span className="text-gray-400 italic flex items-center space-x-1">
                            <Search size={12} className="animate-pulse" />
                            <span>Searching...</span>
                          </span>
                        ) : null
                      )}
                    </div>
                    {lead.dm_linkedin && (
                      <div className="mt-1">
                        <a 
                          href={lead.dm_linkedin.startsWith('http') ? lead.dm_linkedin : `https://${lead.dm_linkedin}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="LinkedIn Profile"
                        >
                          <Linkedin size={16} />
                        </a>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <div className="space-y-2">
                      {lead.company_phone && (
                        <div className="flex items-center space-x-2 text-sm text-gray-900">
                          <Phone size={14} className="text-gray-500" />
                          <span>{lead.company_phone}</span>
                        </div>
                      )}
                      {!lead.company_phone && (
                        <div className="flex items-center space-x-2 text-sm text-gray-400 italic">
                          <Phone size={14} className="text-gray-400" />
                          <Search size={12} className="animate-pulse" />
                          <span>Searching...</span>
                        </div>
                      )}
                      {lead.dm_mobile_1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-900">
                          <Smartphone size={14} className="text-gray-500" />
                          <span>{lead.dm_mobile_1}</span>
                        </div>
                      )}
                      {!lead.dm_mobile_1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-400 italic">
                          <Smartphone size={14} className="text-gray-400" />
                          <Search size={12} className="animate-pulse" />
                          <span>Searching...</span>
                        </div>
                      )}
                      {lead.dm_email_1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-900">
                          <Mail size={14} className="text-gray-500" />
                          <span>{lead.dm_email_1}</span>
                        </div>
                      )}
                      {!lead.dm_email_1 && (
                        <div className="flex items-center space-x-2 text-sm text-gray-400 italic">
                          <Mail size={14} className="text-gray-400" />
                          <Search size={12} className="animate-pulse" />
                          <span>Searching...</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-gray-700 w-12 text-xs">Company</span>
                        {lead.company_qualified ? (
                          <CheckCircle size={14} className="text-green-600" />
                        ) : (
                          <X size={14} className="text-red-600" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-gray-700 w-12 text-xs">Person</span>
                        {lead.dm_qualified ? (
                          <CheckCircle size={14} className="text-green-600" />
                        ) : (
                          <X size={14} className="text-red-600" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-gray-700 w-12 text-xs">Mobile</span>
                        {lead.mobile_verified ? (
                          <CheckCircle size={14} className="text-green-600" />
                        ) : (
                          <X size={14} className="text-red-600" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-gray-700 w-12 text-xs">Email</span>
                        {lead.email_verified ? (
                          <CheckCircle size={14} className="text-green-600" />
                        ) : (
                          <X size={14} className="text-red-600" />
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-gray-700 w-12 text-xs">LinkedIn</span>
                        {lead.linkedin_verified ? (
                          <CheckCircle size={14} className="text-green-600" />
                        ) : (
                          <X size={14} className="text-red-600" />
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    {(() => {
                      const getLeadStatusTag = (status: string) => {
                        switch (status?.toLowerCase()) {
                          case 'ready to go':
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                Ready to Go 🚀
                              </span>
                            );
                          case 'looks good':
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                Looks Good 👌
                              </span>
                            );
                          case 'reach via linkedin':
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                Reach via LinkedIn 🧑‍💼
                              </span>
                            );
                          case 'needs enrichment':
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                                Needs Enrichment 🧩
                              </span>
                            );
                          case 'not a fit':
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                Not a Fit ❌
                              </span>
                            );
                          default:
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                               {status || 'In Review🔍'}
                              </span>
                            );
                        }
                      };
                      
                      return getLeadStatusTag(lead.lead_status);
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-white rounded-xl border border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredLeads.length)} of {filteredLeads.length} results
          </div>
          {totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {paginatedLeads.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">No pre-qualified leads found</div>
          <div className="text-gray-400 text-sm mt-1">
            {searchTerm || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Pre-qualified leads will appear here once validation is complete'}
          </div>
        </div>
      )}
    </div>
  );
};

export default PreQualifiedLeadsPage;